// Enhanced ContentUtils.js with HTML processing capabilities

export const hasValidContent = (content) => {
  if (!content) return false;
  const cleaned = content.toString().trim().toLowerCase();
  return (
    cleaned !== "" &&
    cleaned !== "null" &&
    cleaned !== "undefined" &&
    cleaned !== '""' &&
    cleaned !== "''"
  );
};

export const getLocalizedField = (job, language, fieldName) => {
  const primary = language === "en" ? job[fieldName] : job[`$t:${fieldName}`];
  const fallback = language === "en" ? job[`$t:${fieldName}`] : job[fieldName];

  return hasValidContent(primary)
    ? primary
    : hasValidContent(fallback)
    ? fallback
    : "";
};

// Check if content contains HTML tags
export const isHTMLContent = (content) => {
  if (!hasValidContent(content)) return false;
  return /<[^>]+>/g.test(content);
};

// Enhanced formatContent that handles both plain text and HTML
export const formatContent = (content) => {
  if (!hasValidContent(content)) return "";

  // If content contains HTML tags, return it as-is for dangerouslySetInnerHTML
  if (isHTMLContent(content)) {
    return content
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\r/g, "\r")
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, "\\")
      .trim();
  }

  // Original formatting for plain text
  return content
    .replace(/\\n/g, "\n")
    .replace(/\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\t/g, "    ")
    .replace(/\\r/g, "\r")
    .replace(/\r/g, "")
    .replace(/\\"/g, '"')
    .replace(/"/g, "")
    .replace(/\\'/g, "'")
    .replace(/'/g, "")
    .replace(/\\\\/g, "\\")
    .replace(/\\b/g, "\b")
    .replace(/\\f/g, "\f")
    .replace(/\\v/g, "\v")
    .replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    .replace(/\s+$/gm, "")
    .replace(/^\s+$/gm, "")
    .replace(/●\\t/g, "● ")
    .replace(/•\\t/g, "• ")
    .replace(/\*\\t/g, "* ")
    .replace(/○\\t/g, "○ ")
    .trim();
};

// Component to safely render HTML content
export const HTMLContent = ({ content, className = "", style = {} }) => {
  if (!hasValidContent(content)) return null;

  if (isHTMLContent(content)) {
    return (
      <div
        className={className}
        style={style}
        dangerouslySetInnerHTML={{ __html: formatContent(content) }}
      />
    );
  }

  // For plain text content, preserve line breaks
  return (
    <div className={className} style={{ whiteSpace: "pre-line", ...style }}>
      {formatContent(content)}
    </div>
  );
};

// Utility for processing list content (handles both HTML and plain text)
export const getFormattedList = (content) => {
  if (!hasValidContent(content)) return [];

  const formattedContent = formatContent(content);

  if (isHTMLContent(formattedContent)) {
    // For HTML content, we'll return it as a single item since it's already formatted
    return [formattedContent];
  }

  // For plain text, split into list items
  return formattedContent
    .split("\n")
    .filter((item) => item.trim() !== "")
    .map((item) => item.trim());
};

// Utility to strip HTML tags if needed (for plain text extraction)
export const stripHTMLTags = (content) => {
  if (!hasValidContent(content)) return "";
  return content.replace(/<[^>]*>/g, "").trim();
};